import { useApolloClient, useQuery } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import { READ_CUSTOMER_SUBSCRIPTIONS } from 'graphql/queries/readCustomerSubscriptions';
import { READ_SALES_ORDER_STATUS } from 'graphql/queries/readSalesOrderStatus';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import {
  Query,
  QueryReadSalesOrderArgs,
  SalesOrderStatus,
} from 'types/generatedSchemaTypes';

const usePollSalesOrderStatus = (
  salesOrderId: string | undefined,
  orderStatus: SalesOrderStatus,
  isSubscription = false
) => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const { startPolling, stopPolling } = useQuery<
    Query,
    QueryReadSalesOrderArgs
  >(READ_SALES_ORDER_STATUS, {
    variables: {
      salesOrderId: salesOrderId!,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip:
      orderStatus === 'ACTIVE' ||
      orderStatus === 'WAITING_FOR_ACTIVATION' ||
      orderStatus === 'WAITING_FOR_PAYMENT' ||
      orderStatus === 'TERMINATED' ||
      orderStatus === 'WAITING_FOR_CSR_APPROVAL' ||
      !salesOrderId,
    onCompleted: async (data) => {
      const isActive = data.readSalesOrder?.orderStatus === 'ACTIVE';
      // Refetch customer subscriptions when status becomes active
      if (isSubscription && isActive) {
        await client.refetchQueries({
          include: [READ_CUSTOMER_SUBSCRIPTIONS],
        });
        return showToast(t('success.updateSubscription'), 'success');
      }

      if (isActive) {
        if (usePermitStore.getState().wasPermitTerminated) {
          usePermitStore.getState().setWasPermitTerminated(false);
          return showToast(t('success.permitCancelled'), 'success');
        }
        return showToast(t('success.updatePermit'), 'success');
      }
    },
  });

  useEffect(() => {
    if (
      (orderStatus === 'ACTIVATING' || orderStatus === 'UPDATING') &&
      salesOrderId
    ) {
      startPolling(2500);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [orderStatus, salesOrderId, startPolling, stopPolling]);
};

export default usePollSalesOrderStatus;
